import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getHotTopics } from '@/lib/community';
import { excerpt, formatCount, cn } from '@/lib/format';
import CategoryBadge from '@/components/feature/CategoryBadge';

const hotTopics = getHotTopics(5);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % hotTopics.length), 5500);
    return () => clearInterval(timer);
  }, []);

  const go = (next: number) => setIndex((next + hotTopics.length) % hotTopics.length);

  return (
    <section className="w-full px-4 md:px-6 lg:px-8 pt-6 md:pt-8">
      <div className="relative w-full h-[460px] md:h-[540px] rounded-xl overflow-hidden bg-foreground-900">
        {hotTopics.map((topic, i) => (
          <div
            key={topic.id}
            className={cn(
              'absolute inset-0 transition-opacity duration-700',
              i === index ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          >
            <img src={topic.cover} alt={topic.title} title={`${topic.title} - 蜜糖社区热门话题`} className="w-full h-full object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/20" />
          </div>
        ))}

        <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-10 lg:p-12">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 text-background-50 px-3 py-1 text-xs font-label font-bold mb-4">
              <i className="ri-fire-fill" />
              今日热门焦点
            </span>
            <div className="mb-3">
              <CategoryBadge name={hotTopics[index].category.name} color={hotTopics[index].category.color} icon={hotTopics[index].category.icon} />
            </div>
            <h2 className="text-2xl md:text-4xl font-extrabold text-white leading-tight">
              <Link to={`/topic/${hotTopics[index].id}`} className="cursor-pointer hover:text-primary-200 transition-colors">
                {hotTopics[index].title}
              </Link>
            </h2>
            <p className="mt-3 text-sm md:text-base text-white/85 leading-relaxed max-w-xl line-clamp-2">
              {excerpt(hotTopics[index].summary, 110)}
            </p>
            <div className="mt-4 flex items-center gap-5 text-xs md:text-sm text-white/75">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {hotTopics[index].author.name}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line" />
                {formatCount(hotTopics[index].replyCount)} 回复
              </span>
              <span className="hidden sm:flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatCount(hotTopics[index].viewCount)} 浏览
              </span>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const kw = keyword.trim();
              if (kw) navigate(`/search?q=${encodeURIComponent(kw)}`);
            }}
            className="mt-7 w-full max-w-xl flex items-center gap-2 rounded-lg bg-background-50/95 backdrop-blur p-1.5"
          >
            <span className="w-9 h-9 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="想聊点什么？搜索话题关键词…"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-5 py-2.5 whitespace-nowrap transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>

        {/* Controls */}
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/85 text-foreground-900 flex items-center justify-center hover:bg-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/85 text-foreground-900 flex items-center justify-center hover:bg-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        <div className="absolute bottom-5 right-6 md:right-10 z-10 flex items-center gap-2">
          {hotTopics.map((topic, i) => (
            <button
              key={topic.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={cn(
                'h-1.5 rounded-full transition-all cursor-pointer',
                i === index ? 'w-7 bg-primary-400' : 'w-1.5 bg-white/60 hover:bg-white',
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
}